import React from 'react';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { formatCurrencyIntl } from '../../utils/currencyUtils';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';

const COLOR_CLASS_MAP = {
  blue: { text600: 'text-blue-600', text700: 'text-blue-700', text800: 'text-blue-800', bg500: 'bg-blue-500', bg50: 'bg-blue-50', border200: 'border-blue-200' },
  green: { text600: 'text-green-600', text700: 'text-green-700', text800: 'text-green-800', bg500: 'bg-green-500', bg50: 'bg-green-50', border200: 'border-green-200' },
  yellow: { text600: 'text-yellow-600', text700: 'text-yellow-700', text800: 'text-yellow-800', bg500: 'bg-yellow-500', bg50: 'bg-yellow-50', border200: 'border-yellow-200' },
  red: { text600: 'text-red-600', text700: 'text-red-700', text800: 'text-red-800', bg500: 'bg-red-500', bg50: 'bg-red-50', border200: 'border-red-200' }
};

const getColorClasses = (color) => COLOR_CLASS_MAP[color] || COLOR_CLASS_MAP.blue;

// Common utility functions
export const formatCurrency = (amount, currencyCode = 'INR') => {
  return formatCurrencyIntl(amount, currencyCode);
};

export const formatNumber = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

export const formatDate = (date, currentCompany = null) => {
  return formatDateToCompanyFormat(date, currentCompany);
};

export const handleRowClick = (item) => {
  console.log('Open document:', item.id);
};

// Shared Print Function with Company Letterhead
export const printReportWithLetterhead = (reportTitle, reportData, columns, companyDetails = {}, additionalInfo = {}) => {
  const printWindow = window.open('', '_blank');
  
  // Build company details HTML - more compact layout
  const companyDetailsHTML = `
    <div class="letterhead">
      <div class="company-name">${companyDetails?.firmName || 'Company Name'}</div>
      <div class="company-details">
        ${companyDetails?.address && companyDetails?.city && companyDetails?.state ? 
          `<div>${companyDetails.address}, ${companyDetails.city}, ${companyDetails.state} ${companyDetails?.pincode || ''}</div>` : 
          companyDetails?.address ? `<div>${companyDetails.address}</div>` : ''
        }
        ${companyDetails?.gstin || companyDetails?.contactNumber || companyDetails?.email ? 
          `<div class="contact-line">
            ${companyDetails?.gstin ? `<span>GSTIN: ${companyDetails.gstin}</span>` : ''}
            ${companyDetails?.contactNumber ? `<span>Contact: ${companyDetails.contactNumber}</span>` : ''}
            ${companyDetails?.email ? `<span>Email: ${companyDetails.email}</span>` : ''}
          </div>` : ''
        }
      </div>
    </div>
  `;

  // Build additional info HTML - grid layout (2 rows, 3 columns)
  const additionalInfoHTML = Object.keys(additionalInfo).length > 0 ? `
    <div class="report-details">
      <div class="report-details-grid">
        ${Object.entries(additionalInfo).map(([key, value]) => {
          // Handle dateRange object specially
          let displayValue = value;
          if (key === 'dateRange' && value && typeof value === 'object') {
            if (value.start && value.end) {
              displayValue = `${new Date(value.start).toLocaleDateString('en-IN')} to ${new Date(value.end).toLocaleDateString('en-IN')}`;
            } else {
              displayValue = 'Date range not specified';
            }
          }
          return `<div class="detail-item"><strong>${key}:</strong> ${displayValue}</div>`;
        }).join('')}
      </div>
    </div>
  ` : '';

  // Build table HTML
  const tableHTML = `
    <table>
      <thead>
        <tr>
          ${columns.map(col => `<th>${col.label}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${reportData.map(row => `
          <tr>
            ${columns.map(col => {
              const cellValue = col.render ? col.render(row[col.key], row) : row[col.key];
              const cellClass = col.cellClassName || '';
              return `<td class="${cellClass}">${cellValue}</td>`;
            }).join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 12px;
          }
          .letterhead { 
            text-align: center; 
            margin-bottom: 20px; 
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
          }
          .company-name { 
            font-size: 22px; 
            font-weight: bold; 
            margin-bottom: 8px; 
            color: #1f2937;
          }
          .company-details { 
            font-size: 11px; 
            color: #6b7280; 
            line-height: 1.3;
          }
          .contact-line {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 3px;
          }
          .contact-line span {
            white-space: nowrap;
          }
          .report-title { 
            font-size: 18px; 
            font-weight: bold; 
            text-align: center; 
            margin: 15px 0; 
            color: #1f2937;
          }
          .report-details { 
            margin: 10px 0; 
            padding: 8px;
            background-color: #f9fafb;
            border-radius: 6px;
            border-left: 4px solid #3b82f6;
          }
          .report-details-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            gap: 6px;
            margin-top: 3px;
          }
          .detail-item {
            text-align: center;
            padding: 4px 6px;
            background-color: #e9ecef;
            border-radius: 4px;
            font-size: 10px;
            line-height: 1.1;
          }
          .report-details div {
            margin: 3px 0;
            font-size: 11px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 15px; 
            font-size: 10px;
            table-layout: fixed;
          }
          th, td { 
            border: 1px solid #d1d5db; 
            padding: 6px; 
            text-align: left; 
            word-wrap: break-word;
            overflow: hidden;
          }
          th { 
            background-color: #f3f4f6; 
            font-weight: bold; 
            color: #374151;
          }
          tr:nth-child(even) {
            background-color: #f9fafb;
          }
          tr:hover {
            background-color: #f3f4f6;
          }
          .text-green-600 { color: #059669; }
          .text-red-600 { color: #dc2626; }
          .text-yellow-600 { color: #d97706; }
          .bg-gray-50 { background-color: #f9fafb; }
          .font-bold { font-weight: bold; }
          @media print { 
            body { margin: 0; }
            .letterhead { border-bottom: 2px solid #000; }
            .report-details { margin: 6px 0; padding: 6px; }
            .detail-item { padding: 3px 4px; }
            table { margin-top: 8px; }
            th, td { padding: 3px; }
          }
        </style>
      </head>
      <body>
        ${companyDetailsHTML}
        
        <div class="report-title">${reportTitle.toUpperCase()}</div>
        
        ${additionalInfoHTML}
        
        ${tableHTML}
      </body>
    </html>
  `;
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { 
    printWindow.print(); 
    printWindow.close(); 
  }, 800);
};

// Report Header Component
export const ReportHeader = ({ title, subtitle, children }) => (
  <div className="mb-6">
    <h2 className="text-xl font-bold text-gray-800 mb-2">{title}</h2>
    <p className="text-gray-600">{subtitle}</p>
    {children}
  </div>
);

// Summary Cards Component - Single Line Mobile Optimized
export const SummaryCards = ({ cards }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3 sm:mb-4">
    {cards.map((card, index) => (
      <div key={index} className="bg-white border border-gray-200 rounded p-1 sm:p-2 text-center">
        <div className={`text-xs font-medium ${getColorClasses(card.color).text600}`}>{card.label}</div>
        <div className={`text-sm font-bold ${getColorClasses(card.color).text800}`}>{card.value}</div>
        <div className={`h-0.5 w-full rounded-full mt-1 ${getColorClasses(card.color).bg500}`}></div>
      </div>
    ))}
  </div>
);

// Collapsible totals — hidden by default so reports can be shown to customers safely
export const CollapsibleQuickTotals = ({ cards, label = 'Show Totals', defaultOpen = false }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  if (!cards || cards.length === 0) return null;

  return (
    <div className="mb-3 sm:mb-4">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full sm:w-auto min-w-[180px] bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-4 py-2.5 rounded-lg flex items-center justify-between gap-3 font-medium text-sm transition-colors"
        aria-expanded={isOpen}
      >
        <span>{isOpen ? 'Hide Totals' : label}</span>
        <span className={`text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="mt-2">
          <SummaryCards cards={cards} />
        </div>
      )}
    </div>
  );
};

// Backward-compatible alias
export const MobileQuickTotals = CollapsibleQuickTotals;

// Mobile-Friendly Modal Component
export const MobileModal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl'
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[92dvh] sm:max-h-[90vh] overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900 pr-2">{title}</h3>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 text-2xl font-bold touch-manipulation"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[calc(92dvh-4.5rem)] sm:max-h-[calc(90vh-80px)] pb-[max(1rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>
  );
};

// Report Header with Export Buttons Component
export const ReportHeaderWithExport = ({ title, subtitle, children, exportButtons }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-bold text-gray-900 truncate">{title}</h2>
        {subtitle && (
          <p className="text-xs text-gray-600 mt-1 truncate">{subtitle}</p>
        )}
      </div>
      
      <div className="w-full sm:w-auto flex justify-end sm:justify-start flex-shrink-0 relative overflow-visible">
        {exportButtons}
      </div>
    </div>
    
    {/* Additional Content */}
    {children && (
      <div className="mt-4">
        {children}
      </div>
    )}
  </div>
);

// Report Table Component - Mobile Optimized
export const ReportTable = ({ 
  data, 
  columns, 
  sortConfig, 
  onSort, 
  onRowClick, 
  pagination,
  emptyMessage = "No data found",
  emptySubMessage = "Try adjusting your filters or date range"
}) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500 text-lg mb-2">{emptyMessage}</div>
        <p className="text-gray-400">{emptySubMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Mobile Card View */}
      <div className="block sm:hidden">
        {data.map((row, index) => (
          <div
            key={row.id || index}
            className="border-b border-gray-200 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onRowClick && onRowClick(row)}
          >
            <div className="grid grid-cols-1 gap-2">
              {columns.slice(0, 2).map((column, colIndex) => (
                <div key={colIndex} className="flex justify-between">
                  <span className="text-xs text-gray-500 font-medium">{column.label}:</span>
                  <span className="text-sm font-medium text-right break-words pl-2 min-w-0">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </span>
                </div>
              ))}
              {columns.length > 2 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {columns.slice(2).map((column, colIndex) => (
                    <div key={colIndex + 2} className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium">{column.label}</span>
                      <span className="text-sm font-medium">
                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                column.sortable ? (
                  <SortableHeader
                    key={index}
                    columnKey={column.key}
                    label={column.label}
                    onSort={onSort}
                    sortConfig={sortConfig}
                    className={column.className}
                  />
                ) : (
                  <th key={index} className={`px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}>
                    {column.label}
                  </th>
                )
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr
                key={row.id || index}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className={`px-3 sm:px-6 py-4 whitespace-nowrap ${column.cellClassName || ''}`}>
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && <PaginationControls {...pagination} />}
    </div>
  );
};

// Loading Component
export const LoadingSpinner = ({ message = "Loading report data..." }) => (
  <div className="p-8 text-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
    <p className="text-gray-600">{message}</p>
  </div>
);

// Info Box Component
export const InfoBox = ({ type = 'info', title, children }) => {
  const colors = {
    info: 'blue',
    warning: 'yellow',
    success: 'green',
    error: 'red'
  };
  
  const icons = {
    info: (
      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    success: (
      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    )
  };

  const color = colors[type];
  const icon = icons[type];

  return (
    <div className={`mt-6 p-4 rounded-lg border ${getColorClasses(color).bg50} ${getColorClasses(color).border200}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="ml-3">
          <h3 className={`text-sm font-medium ${getColorClasses(color).text800}`}>{title}</h3>
          <div className={`mt-2 text-sm ${getColorClasses(color).text700}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// Back Button Component
export const BackButton = ({ onClick, children }) => (
  <div className="mb-4">
    <button
      onClick={onClick}
      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-300"
    >
      {children}
    </button>
  </div>
); 